import { useSearchParams } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { SumsubVerification, type ExtractedData } from '@/components/onboarding/SumsubVerification';
import { VerificationConfirmScreen, type ConfirmedPersonData } from '@/components/onboarding/VerificationConfirmScreen';
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Public standalone identity-verification page.
 * Reached via the SMS / WhatsApp / email link sent to stakeholders
 * during corporate onboarding.
 *
 * URL format:  /verify?person=<personId>
 *
 * After Sumsub completes, shows the 16-field VerificationConfirmScreen
 * so the person can review and confirm their extracted data before finishing.
 */
export default function VerifyIdentityPage() {
  const [params] = useSearchParams();
  const rawPersonParam = params.get('person') || '';
  // The link token may include a timestamp suffix (e.g. "director-0-1710000000000").
  // Strip it to get the stable personId used for DB lookups, but keep the full token
  // as the Sumsub userId so each link creates a fresh applicant (avoids "Verification expired").
  const personId = rawPersonParam.replace(/-\d{13}$/, ''); // strip trailing -<13-digit-timestamp>
  const sumsubUserId = rawPersonParam; // full token including timestamp → unique Sumsub applicant
  const [stage, setStage] = useState<'sumsub' | 'confirm' | 'done'>('sumsub');
  const [extractedData, setExtractedData] = useState<ExtractedData | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [sumsubError, setSumsubError] = useState<string | null>(null);

  const handleSumsubComplete = (data?: ExtractedData) => {
    setExtractedData(data);
    setStage('confirm');
  };

  const handleSumsubError = (err: string) => {
    setSumsubError(err);
    setError(null); // show the retry UI, not the hard error screen
  };

  const handleGenerateNewLink = () => {
    // Reload the page with a fresh timestamp appended to the person param.
    // VerifyIdentityPage strips the old timestamp and appends a new one,
    // ensuring sumsub-token creates a completely new applicant.
    const freshToken = `${personId}-${Date.now()}`;
    const url = new URL(window.location.href);
    url.searchParams.set('person', freshToken);
    window.location.href = url.toString();
  };

  if (stage === 'done') {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-success" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Verification Complete</h1>
        <p className="text-muted-foreground max-w-sm">
          Thank you — your identity has been verified. You can close this window.
        </p>
      </div>
    );
  }

  if (error) {
    const isExpired = /expired|access denied|blocked|denied/i.test(error);
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
          <Shield className="w-8 h-8 text-destructive" />
        </div>
        <h1 className="text-xl font-bold text-destructive mb-2">Verification Error</h1>
        <p className="text-muted-foreground mb-6 max-w-sm">{error}</p>
        {isExpired && personId && (
          <button
            onClick={() => {
              const freshToken = `${personId}-${Date.now()}`;
              const url = new URL(window.location.href);
              url.searchParams.set('person', freshToken);
              window.location.href = url.toString();
            }}
            className="px-6 py-3 rounded-full bg-accent text-accent-foreground font-semibold hover:bg-accent/90 transition-all mb-4"
          >
            Try Again with Fresh Link
          </button>
        )}
        <p className="text-xs text-muted-foreground max-w-sm">
          If you see "Access denied", try disabling any VPN or proxy. Some mobile carriers route traffic through proxies that verification services block.
        </p>
      </div>
    );
  }

  if (!personId) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 text-center">
        <h1 className="text-xl font-bold text-foreground mb-2">Invalid Link</h1>
        <p className="text-muted-foreground">This verification link is incomplete. Please check your message and try again.</p>
      </div>
    );
  }

  // ── Confirm screen (after Sumsub) ──────────────────────────────────
  // Save confirmed data via service-role edge function (RLS blocks unauthenticated direct writes)
  const handleConfirm = async (confirmed: ConfirmedPersonData) => {
    try {
      await supabase.functions.invoke('save-person-kyc', {
        body: { personId, confirmedData: confirmed },
      });
    } catch (e) {
      console.error('VerifyIdentityPage: failed to save confirmed data', e);
    }
    setStage('done');
  };

  if (stage === 'confirm') {
    const extractedFirstName = extractedData?.firstName || '';
    const extractedLastName = extractedData?.lastName || '';
    const personName = (extractedFirstName || extractedLastName)
      ? `${extractedFirstName} ${extractedLastName}`.trim()
      : 'Stakeholder';
    return (
      <VerificationConfirmScreen
        personName={personName}
        knownFirstName={extractedFirstName || undefined}
        knownLastName={extractedLastName || undefined}
        extractedData={extractedData}
        onConfirm={handleConfirm}
      />
    );
  }

  // ── Sumsub verification ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="px-4 py-5 border-b border-border flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
          <Shield className="w-4 h-4 text-accent" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">PayNovus</p>
          <p className="text-xs text-muted-foreground">Identity Verification</p>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 px-4 py-6 max-w-lg mx-auto w-full">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-1">Verify Your Identity</h1>
          <p className="text-muted-foreground text-sm">
            Complete the steps below to verify your identity as part of a business account application.
          </p>
        </div>

        <SumsubVerification
          onComplete={handleSumsubComplete}
          onError={(err) => {
            if (/expired/i.test(err)) {
              setError('Your verification session has expired. Please click "Try Again" to start a fresh verification.');
            } else if (/denied|blocked/i.test(err)) {
              setError('Access was blocked by the verification provider. This can happen if you are using a VPN, proxy, or certain mobile networks. Please try on a different network or disable your VPN.');
            } else {
              handleSumsubError(err);
            }
          }}
          levelName="AI Onboarding"
          includePOA={true}
          personId={sumsubUserId}
        />

        {/* Retry UI shown when Sumsub reports an error (e.g. "Verification expired") */}
        {sumsubError && (
          <div className="mt-6 p-4 rounded-lg border border-destructive/30 bg-destructive/5 text-center">
            <p className="text-sm text-destructive font-medium mb-1">Verification could not be loaded</p>
            <p className="text-xs text-muted-foreground mb-4">
              This can happen if the verification session expired. Generate a fresh link to start a new session.
            </p>
            <button
              onClick={handleGenerateNewLink}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 transition-colors"
            >
              Generate New Link
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
